import { useHistory } from "react-router-dom";
import { useContext, useMemo, useState } from "react";
import { SessionContext } from "../../common/contexts/SessionContext";
import { SearchInput } from "../../common/forms/SearchInput/SearchInput";
import { LucideIcon } from "../../common/icon/LucideIcon";
import TableView, { TableViewLabel } from "../../common/panels/TableView/TableView";
import { ColumnsSettings } from "../../common/tables/ColumnsSelector/ColumnsSelector";
import { AlertModal } from "../../common/modals/AlertModal/AlertModal";
// import { showtoast } from "../../common/overlays/Toasts/showtoast";
import { MoreDropdown } from "../../common/buttons/MoreDropdown/MoreDropdown";
import { EntityTable } from "../../common/tables/EntityTable/EntityTable";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { EntityFilterIndicator } from "../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import { useTabStore } from "../../common/tables/Tabs/useTabStore";
import { TableTabsDict } from "../../common/tables/Tabs/TableTabsTypes";
import { Role, RoleFilters, RoleFiltersTranslator } from "../../api/Role";
import { RoleFilterBar, RoleFilterForm } from "./RolesFilterBar";
import styles from "./RolesTable.module.css";
import { NotSet } from "../../common/misc/UIconstants";
import { DateTimeRenderer } from "../../common/datetime/DateTimeFormatter";
import { GetPersons } from "../../common/misc/EntityRenders/EntityRenderer";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  UseEntityTableColumns,
  UseEntityTableDefaultProps,
  UseEntityTableProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
} from "../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { EntityTableProps } from "../../common/entity/EntityInterfaces";
import { EntityTableDeleteButton } from "../../common/entity/entityComponents/EntityTableDeleteButton";
import { EntityTableCloneButton } from "../../common/entity/entityComponents/EntityTableCloneButton";
import { EntityTableEditButton } from "../../common/entity/entityComponents/EntityTableEditButton";
import { EntityOrderBy, InferSortByOptions } from "../../api/GenericTypes";

export const switchRolesDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<RoleFilters>>[] | null | undefined
): SortState<InferSortByOptions<RoleFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;
  return [{ headerId: "default-name", sortDirection: "DESC", property: "NAME" }];
};
const defaultRolesFilters: RoleFilters = {
  sortBy: [
    {
      property: "NAME",
      sortDirection: "ASC",
    },
  ],
};

export const useRolesTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"roles">) => {
  const defaults: ColumnsSettings<Role> = useMemo(
    () => ({
      "default-id": { pos: 0, active: false, header: fieldLabels.id, property: "id" },
      "default-name": { pos: 1, active: true, header: fieldLabels.name, property: "name" },
      "default-description": { pos: 2, active: true, header: fieldLabels.description, property: "description" },
      "default-enteredOn": { pos: 3, active: true, header: fieldLabels.enteredOn, property: "enteredOn" },
      "default-enteredBy": { pos: 4, active: true, header: fieldLabels.enteredBy, property: "enteredBy" },
      "default-modifiedOn": { pos: 5, active: true, header: fieldLabels.modifiedOn, property: "modifiedOn" },
      "default-modifiedBy": { pos: 6, active: true, header: fieldLabels.modifiedBy, property: "modifiedBy" },
    }),
    [fieldLabels]
  );

  const tabStoreDefaults: TableTabsDict<Role, RoleFilters, RoleFilterForm> = useMemo(
    () => ({
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultRolesFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
      },
    }),
    []
  );
  return { defaults, tabStoreDefaults };
};

export const useRolesTableColumns: UseEntityTableColumns<"roles"> = ({
  entityConstants,
  fieldLabels,
  sort,
  setSort,
}: UseEntityTableProps<"roles">) => {
  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<Role> = [
      {
        id: "default-id",
        Header: fieldLabels.id,
        accessor: (row) => <span style={{ color: "var(--gray-400)" }}>{row.id}</span>,
        width: 120,
        minWidth: 120,
        align: "center",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        Header: fieldLabels.name,
        id: "default-name",
        accessor: (row) => (
          <div className="flex gap-5" style={{ height: "max-content" }} data-toggle="tooltip" title={row.name}>
            <div className="flex align-center gap-5">
              <LucideIcon name={entityConstants.icon} color="var(--primary)" />
              <div
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  marginRight: "5px",
                }}
              >
                {row.name}
              </div>
            </div>
          </div>
        ),
        minWidth: 150,
        width: 250,
        align: "left",
        ...getAsyncSortingFn("NAME", sort, setSort),
      },
      {
        Header: fieldLabels.description,
        id: "default-description",
        accessor: (row) => (
          <div title={row.description} className={styles.description}>
            {row.description || NotSet}
          </div>
        ),
        minWidth: 150,
        width: 800,
        align: "left",
      },
      {
        id: "default-enteredOn",
        Header: fieldLabels.enteredOn,
        accessor: (row) => <DateTimeRenderer date={row.enteredOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_ON", sort, setSort),
      },
      {
        id: "default-enteredBy",
        Header: fieldLabels.enteredBy,
        accessor: (row) => <GetPersons persons={row.enteredBy} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_BY", sort, setSort),
      },
      {
        id: "default-modifiedOn",
        Header: fieldLabels.modifiedOn,
        accessor: (row) => <DateTimeRenderer date={row.modifiedOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_ON", sort, setSort),
      },
      {
        id: "default-modifiedBy",
        Header: fieldLabels.modifiedBy,
        accessor: (row) => <GetPersons persons={row.modifiedBy} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_BY", sort, setSort),
      },
    ];

    return headers;
  }, [
    fieldLabels.id,
    fieldLabels.name,
    fieldLabels.description,
    fieldLabels.enteredOn,
    fieldLabels.enteredBy,
    fieldLabels.modifiedOn,
    fieldLabels.modifiedBy,
    sort,
    setSort,
    entityConstants.icon,
  ]);
  return { columns };
};

export const RolesTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
}: EntityTableProps<"roles">) => {
  const history = useHistory();
  const { session } = useContext(SessionContext);
  const [showModal, setShowModal] = useState(false);
  const { bulkDeleteMutationAsync } = entityApi;

  const { defaults, tabStoreDefaults } = useRolesTableDefaults({ fieldLabels });

  const { selection, resultsCount, selectionPermissions, onCountChange, onSelectionChange, onSelectionPermissions } =
    useGenericVirtualizedTable<Role>();

  const {
    filters,
    sidebarFilters,
    forcedFilters,
    columnSetting,
    columnWidths,
    dispatchTabStore,
    currentTab,
    tabsLoading,
  } = useTabStore<Role, RoleFilters, RoleFilterForm>({
    resource: entityConstants.resource,
    defaults: tabStoreDefaults,
  });

  const { functionRef, sort, setSort, searchValue, setSearchValue } = useGenericVirtualizedTableTabs({
    currentTab,
    tabsLoading,
    filters,
    switchSortState: switchRolesDefaultSortState,
    dispatchTabStore,
  });

  const { columns } = useRolesTableColumns({ fieldLabels, entityConstants, sort, setSort });

  const memoizedFilters = useMemo(() => ({ ...filters, ...forcedFilters }), [filters, forcedFilters]);

  return (
    <TableView>
      <TableView.Head>
        <TableView.Head.Label>
          <TableViewLabel entityConstants={entityConstants} resultsCount={resultsCount} />
        </TableView.Head.Label>
        <TableView.Head.Controls>
          {session?.permissions.logsadmin && (
            <button
              className="btn btn-primary"
              title="Add Role"
              onClick={() => history.push(routes.getAddRoute)}
              disabled={!permissions.canCreate}
            >
              <LucideIcon name="plus" /> Add Role
            </button>
          )}
        </TableView.Head.Controls>
      </TableView.Head>
      <TableView.Body>
        {(session?.permissions.logsadmin || session?.permissions.view_admin_pages) && (
          <TableView.Body.Sidebar>
            <RoleFilterBar
              initialValues={sidebarFilters}
              dispatchTabStore={dispatchTabStore}
              tabsLoading={tabsLoading}
              currentTab={currentTab}
            />
          </TableView.Body.Sidebar>
        )}
        <TableView.Body.Content>
          <EntityTable>
            <EntityTable.Controls
            // style={{
            //   borderTop: "0px",
            //   borderRadius: "0px",
            // }}
            >
              <EntityFilterIndicator<Role, RoleFilters>
                filters={filters}
                excludeFilters={{
                  includeInternals: (value) => !!value,
                }}
                translatorConsts={RoleFiltersTranslator}
              />
              <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" />

              {session?.permissions.logsadmin && (
                <>
                  <EntityTableEditButton
                    entityConstants={entityConstants}
                    routes={routes}
                    permissions={permissions}
                    selection={selection}
                    selectionPermissions={selectionPermissions}
                  />
                  <MoreDropdown drop="right" btn="btn btn-ghost-secondary">
                    <li>
                      <EntityTableCloneButton
                        entityConstants={entityConstants}
                        routes={routes}
                        permissions={permissions}
                        selection={selection}
                      />
                    </li>
                    <li>
                      <EntityTableDeleteButton
                        entityConstants={entityConstants}
                        permissions={permissions}
                        selection={selection}
                        selectionPermissions={selectionPermissions}
                        onClick={() => setShowModal(true)}
                      />
                    </li>
                  </MoreDropdown>
                </>
              )}
              <AlertModal
                type="danger"
                showModal={showModal}
                setShowModal={setShowModal}
                title={`${
                  selection.size === 0
                    ? `Select ${entityConstants.entitySingular} to delete`
                    : selection.size === 1
                    ? `Delete the selected ${entityConstants.entitySingular}`
                    : `Delete the selected ${entityConstants.entityPlural}`
                }`}
                description={`Proceeding will permanently delete the selected ${entityConstants.entityPlural}.`}
                proceedLabel="Delete"
                onProceed={async () => {
                  await bulkDeleteMutationAsync({
                    ids: Array.from(selection),
                    goBackOnSuccess: false,
                    showToast: false,
                    entityName: "roles",
                    params: { deletePermanently: true },
                  }).catch((e) => {});
                  functionRef.current?.resetSelection();
                  setShowModal(false);
                }}
              />
            </EntityTable.Controls>
            <EntityTable.Body<Role, RoleFilters>
              functionRef={functionRef}
              entityConstants={entityConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={defaults}
              dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              onSelectionChange={onSelectionChange}
              onSelectionPermissions={onSelectionPermissions}
              showPermissionColumn={session?.permissions.logsadmin}
              loading={tabsLoading}
              fixedRowHeight={55}
              disableCheckboxes={!session?.permissions.logsadmin}
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
